import React, {
  useState, useEffect, useMemo, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { useToasts } from '../../hooks';
import ImportPicker from '../../componentsV2/Imports/ImportPicker';
import Table from '../../componentsV2/table/Table';
import Pagination from '../../componentsV2/pagination/Pagination';
import { columns, columnTextColor } from '../../utils/imports';
import { getImports } from '../../utils/api/imports';
import { formatImportDate } from '../../utils/dates';
import Icon from '../../componentsV2/Icons/Icon';
import useDeleteImport from './useDeleteImport';

const ImportsPage = ({ organizationId, isManager }) => {
  const [importData, setImportData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isImportsChanged, setIsImportsChanged] = useState(false);
  const { showToast } = useToasts();
  const customError = 'We weren’t able to import your file. Please review the errors and try again.';
  //   pagination
  const limit = 20;
  const offset = (currentPage - 1) * limit;
  const params = useMemo(() => {
    return {
      limit,
      offset,
    };
  }, [limit, offset]);
  const [deleteImportModal] = useDeleteImport({
    organizationId, showToast, customError, setIsImportsChanged,
  });

  // fetch imports
  const fetchImports = useCallback(() => {
    getImports(organizationId, params)
      .then((response) => {
        if (!response.ok) {
          showToast({ message: response.statusText || customError, type: 'error' });
        }
        return response.json();
      })
      .then((data) => {
        if (!data) {
          showToast({ message: customError, type: 'error' });
          return;
        }
        if (Array.isArray(data.import_files) && data.import_files.length > 0) {
          const modifiedData = data.import_files.map((file) => (
            // Format the import json fields
            {
              ...file,
              created_at: formatImportDate(file.created_at),
              updated_at: formatImportDate(file.updated_at),
              action: (
                <div className="flex items-center justify-center">
                  <button type="button" onClick={() => deleteImportModal(file)} aria-label="delete import" data-testid="delete-import">
                    {isManager && <Icon name="delete" />}
                  </button>
                </div>
              ),
            }
          ));
          setImportData(modifiedData);
        } else {
          setImportData(data.import_files);
        }
        setTotalItems(data.total_count);
        setIsImportsChanged(false);
      })
      .catch((error) => {
        showToast({ message: error.message || customError, type: 'error' });
        setImportData([]);
        setTotalItems(0);
        setIsImportsChanged(false);
      });
  }, [organizationId, params, showToast, customError, deleteImportModal, isManager]);

  useEffect(() => {
    fetchImports();
  }, [fetchImports]);

  // handle new import to fetch the imports for table
  useEffect(() => {
    if (isImportsChanged) {
      fetchImports();
    }
  }, [isImportsChanged, fetchImports]);

  // Refetch data when PageHeaderWrapper signals cross-page import completion.
  useEffect(() => {
    window.addEventListener('importCompleted', fetchImports);
    return () => window.removeEventListener('importCompleted', fetchImports);
  }, [fetchImports]);

  return (
    <div className="flex flex-row w-full gap-8 mt-4">
      <ImportPicker organizationId={organizationId} setIsNewImport={setIsImportsChanged} customError={customError} />
      <div className="flex flex-col w-2/3" data-testid="table-wrapper">
        {(totalItems > 0) && <Table typeOfData="imports" columns={columns} dataSource={importData} columnTextColor={columnTextColor} />}
        {(totalItems > limit) && (<Pagination itemsPerPage={limit} totalItems={totalItems} currentPage={currentPage} setCurrentPage={setCurrentPage} marginTop="mt-8" />)}
      </div>
    </div>
  );
};

ImportsPage.propTypes = {
  organizationId: PropTypes.number.isRequired,
  isManager: PropTypes.bool.isRequired,
};

export default ImportsPage;
